import { useState } from 'react';
import Card from 'antd/lib/card';
import Table from 'antd/lib/table';
import Switch from 'antd/lib/switch';
import { ColumnsType } from 'antd/lib/table/interface';
import { EventsByYearMonth, ICalculateEventsResult } from './calculatePriceResponseScenariosEvents';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface IPriceResponseScenariosResultsTableProps {
    events: ICalculateEventsResult;
    isDefinedMaxDispatchesPerDay: boolean;
    isLoading: boolean;
}

interface IRow {
    key: number | string;
    year: number | string;
    total: number;
    isTotalRow?: boolean;
    [month: string]: number | string | boolean | undefined;
}

const buildRows = (events: EventsByYearMonth): IRow[] => {
    const years = Object.keys(events)
        .map(y => parseInt(y, 10))
        .sort((a, b) => a - b);

    const rows = years.map(year => {
        const monthCounts = events[year] || {};
        const row: IRow = { key: year, year, total: 0 };

        for (let month = 0; month < 12; month++) {
            const count = monthCounts[month]?.eventCount || 0;
            row[MONTH_LABELS[month]] = count;
            row.total += count;
        }

        return row;
    });

    if (rows.length > 1) {
        const totalRow: IRow = { key: 'total', year: 'Total', total: 0, isTotalRow: true };

        for (const month of MONTH_LABELS) {
            totalRow[month] = 0;
        }

        for (const row of rows) {
            for (const month of MONTH_LABELS) {
                totalRow[month] = (totalRow[month] as number) + (row[month] as number);
            }
            totalRow.total += row.total;
        }

        rows.push(totalRow);
    }

    return rows;
};

export const PriceResponseScenariosEventsTable = ({ events, isDefinedMaxDispatchesPerDay, isLoading }: IPriceResponseScenariosResultsTableProps) => {
    const [applyMaxDispatchPerDay, setApplyMaxDispatchPerDay] = useState(true);

    const rows = buildRows(applyMaxDispatchPerDay ? events.summaryWithCap : events.summaryWithoutCap);

    const columns: ColumnsType<IRow> = [
        {
            title: 'Year',
            dataIndex: 'year',
            fixed: 'left',
            width: 80,
        },
        ...MONTH_LABELS.map(month => ({
            title: month,
            dataIndex: month,
            align: 'right' as const,
            render: (value: number) => (value ? value : '-'),
        })),
        {
            title: 'Total',
            dataIndex: 'total',
            align: 'right' as const,
            fixed: 'right',
            width: 80,
            render: (value: number) => <strong>{value}</strong>,
        },
    ];

    return (
        <Card
            title="Estimated Events"
            style={{ marginBottom: 16 }}
            loading={isLoading}
            extra={
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span>Apply Max Dispatches Per Day</span>
                    <Switch disabled={!isDefinedMaxDispatchesPerDay} checked={applyMaxDispatchPerDay} onChange={setApplyMaxDispatchPerDay} />
                </label>
            }
        >
            <Table<IRow> size="small" rowKey="key" columns={columns} dataSource={rows} pagination={false} />
        </Card>
    );
};
